import React, { useContext, useEffect, useState } from "react"
import { onAuthStateChanged } from "firebase/auth"
import { auth } from "../services/firebase.service"
import { schoolContext } from "./school.provider"
import { resolveLessonTypes } from "../utils/school.utils"
import { withMatchLessonType } from "../utils/match.utils"
import { withDisplayNames } from "../utils/func.utils"
import {
	getLessonTypes,
	getPaymentMethods,
	getSports,
	getSurfaces,
} from "../utils/firestore.utils"

export const sharedDataContext = React.createContext()

const SharedDataProvider = ({ children }) => {
	const { getSelectedSchool } = useContext(schoolContext)
	const [paymentMethods, setPaymentMethods] = useState({})
	const [lessonTypes, setLessonTypes] = useState({})
	const [sports, setSports] = useState({})
	const [surfaces, setSurfaces] = useState({})
	const [loading, setLoading] = useState(true)

	// I cataloghi sono uguali per tutti, ma da quando ci sono le regole vanno
	// letti da autenticati: chiederli al montaggio dell'app significava
	// chiederli da sconosciuti, e chi arriva sulla pagina pubblica e si
	// registra si sarebbe trovato senza sport e senza metodi di pagamento
	// proprio mentre glieli chiediamo. Si leggono quando c'è un accesso.
	useEffect(() => {
		const fetchAll = async () => {
			setLoading(true)
			try {
				const [paymentMethodsData, lessonTypesData, sportsData, surfacesData] =
					await Promise.all([
						getPaymentMethods(),
						getLessonTypes(),
						getSports(),
						getSurfaces(),
					])
				setPaymentMethods(paymentMethodsData)
				setLessonTypes(lessonTypesData)
				setSports(sportsData)
				setSurfaces(surfacesData)
			} catch (err) {
				console.error("Errore caricando SharedData:", err)
			} finally {
				setLoading(false)
			}
		}

		return onAuthStateChanged(auth, firebaseUser => {
			if (!firebaseUser) {
				setPaymentMethods({})
				setLessonTypes({})
				setSports({})
				setSurfaces({})
				setLoading(false)
				return
			}

			fetchAll()
		})
	}, [])

	// ──────────────────────────────────────────────────────────────────
	// I TIPI DI LEZIONE CHE SI CONOSCONO QUI
	//
	// Il catalogo globale copre la scuola tennis classica; il resto — il live
	// ball, il centro estivo di quel circolo lì — se lo scrive il circolo, e
	// sta sul suo documento, insieme alle correzioni che fa a quelle del
	// catalogo. Chi legge un tipo di lezione non deve sapere da quale dei due
	// arriva: qui si uniscono, e da fuori è un catalogo solo.
	//
	// Il catalogo crudo resta però a disposizione, perché il listino deve
	// poter far vedere cosa dice per conto suo: è quello il punto di partenza
	// che una correzione corregge, ed è quello a cui si torna cancellandola.
	// ──────────────────────────────────────────────────────────────────
	const schoolLessonTypes = getSelectedSchool()?.lessonTypes ?? {}

	// E poi le partite, che il tipo se lo porta l'app: in agenda, nel modulo
	// e nel riepilogo si leggono come le altre, il listino no.
	// con il nome che si legge: i corsi per esteso, o la sigla del circolo
	const knownLessonTypes = withDisplayNames(
		withMatchLessonType(resolveLessonTypes(lessonTypes, schoolLessonTypes)),
	)

	// getter per comodità nei componenti
	const getPaymentMethod = id => paymentMethods[id] || null
	const getLessonType = id => knownLessonTypes[id] || null
	const getSport = id => sports[id] || null
	const getSurface = id => surfaces[id] || null

	return (
		<sharedDataContext.Provider
			value={{
				paymentMethods,
				lessonTypes: knownLessonTypes,
				catalogLessonTypes: lessonTypes,
				sports,
				surfaces,
				getPaymentMethod,
				getLessonType,
				getSport,
				getSurface,
				loading,
			}}>
			{children}
		</sharedDataContext.Provider>
	)
}

export default SharedDataProvider
